import {
  NotificationItem,
  useMarkAllNotificationsReadMutation,
  useNotificationsQuery,
} from "@/actions/notification.actions";
import { Alert, App, Badge, Button, Dropdown, Empty, Tag, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import {
  ArrowRightIcon,
  BellIcon,
  CheckCheckIcon,
  PlusIcon,
} from "lucide-react";
import React, { useState } from "react";
import { useNavigate } from "react-router";
import { CreateReminderModal } from "./CreateReminderModal";
import { ViewNotificationModal } from "./ViewNotificationModal";
import { getNotificationIcon } from "./notificationIcons";

dayjs.extend(relativeTime);

/** Renders the authenticated user's notification surface. */
export const NotificationsDropdown: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useNotificationsQuery();
  const { message } = App.useApp();
  const markAllReadMutation = useMarkAllNotificationsReadMutation();
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const notifications = data?.notifications || [];
  const unreadCount = data?.unreadCount || 0;

  const handleNotificationClick = (item: NotificationItem) => {
    setDropdownOpen(false);
    setSelectedNotification(item);
  };

  const handleMarkAllRead = (e: React.MouseEvent) => {
    e.stopPropagation();
    markAllReadMutation.mutate(undefined, { onError: () => message.error("Failed to mark notifications as read") });
  };

  const menuContent = (
    <div className="w-[calc(100vw-1.5rem)] sm:w-96 max-w-sm rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden text-[#131B2A]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-3.5 sm:px-4 py-2.5 sm:py-3 bg-gray-50/70 gap-2">
        <div className="flex items-center gap-2 min-w-0 shrink">
          <Typography.Text strong className="text-sm !text-[#131B2A] whitespace-nowrap">
            Notifications
          </Typography.Text>
          {unreadCount > 0 ? (
            <Tag
              bordered={false}
              color="gold"
              className="!m-0 !px-2 !py-0.5 !text-xs !font-semibold !rounded-full whitespace-nowrap shrink-0"
            >
              {unreadCount} new
            </Tag>
          ) : null}
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          {unreadCount > 0 ? (
            <Tooltip title="Mark all notifications as read">
              <Button
                type="text"
                size="small"
                aria-label="Mark all notifications as read"
                onClick={handleMarkAllRead}
                loading={markAllReadMutation.isPending}
                icon={<CheckCheckIcon className="h-3.5 w-3.5 text-gray-500" />}
                className="!text-xs text-gray-600 hover:text-[#131B2A] !px-1.5 sm:!px-2"
              >
                <span className="hidden sm:inline">Mark read</span>
              </Button>
            </Tooltip>
          ) : null}

          <Button
            type="primary"
            size="small"
            onClick={() => {
              setDropdownOpen(false);
              setReminderModalOpen(true);
            }}
            icon={<PlusIcon className="h-3.5 w-3.5" />}
            className="!bg-[#131B2A] hover:!bg-[#1E293B] !text-xs !font-medium"
          >
            Reminder
          </Button>
        </div>
      </div>

      {/* Notifications list */}
      <div className="max-h-[min(65vh,24rem)] overflow-y-auto divide-y divide-gray-100 overscroll-contain">
        {isError ? (
          <Alert type="error" message="Unable to load notifications" className="!m-3" action={<Button size="small" onClick={() => refetch()}>Retry</Button>} />
        ) : isLoading ? (
          <div className="py-8 text-center text-xs text-gray-600">Loading notifications...</div>
        ) : notifications.length === 0 ? (
          <div className="py-10 px-4 text-center">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span className="text-xs text-gray-500">No notifications in the last 24 hours</span>}
            />
          </div>
        ) : (
          notifications.map((item) => {
            const isUnread = !item.isRead;
            const timeAgo = dayjs(item.triggeredAt).fromNow();

            return (
              <button
                type="button"
                key={item.id}
                onClick={() => handleNotificationClick(item)}
                className={[
                  "flex w-full items-start gap-3 p-3 transition-colors cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-[#DDA74F] focus-visible:-outline-offset-2",
                  isUnread ? "bg-[#DDA74F]/5 hover:bg-[#DDA74F]/10" : "bg-white hover:bg-gray-50",
                ].join(" ")}
              >
                {getNotificationIcon(item.type, item, "sm")}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={[
                        "text-xs leading-snug line-clamp-1",
                        isUnread ? "font-bold text-[#131B2A]" : "font-medium text-gray-800",
                      ].join(" ")}
                    >
                      {item.title}
                    </p>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {isUnread ? (
                        <span className="h-2 w-2 rounded-full bg-amber-500 shrink-0 mt-0.5" />
                      ) : null}

                    </div>
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2 mt-0.5 leading-relaxed">
                    {item.message}
                  </p>
                  <div className="flex items-center justify-between gap-2 mt-1">
                    <span className="text-[10px] text-gray-600 font-medium">
                      {timeAgo}
                    </span>
                    <span className="text-[10px] font-semibold text-gray-500 hover:text-[#131B2A] hover:underline">
                      View details
                    </span>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Footer Info & View All */}
      <div className="border-t border-gray-100 p-2 bg-gray-50/70 flex flex-col items-center gap-0.5">
        <Button
          type="link"
          size="small"
          onClick={() => {
            setDropdownOpen(false);
            navigate("/notifications");
          }}
          className="!text-xs !font-semibold !text-[#131B2A] hover:!text-(--brand-primary) flex items-center justify-center gap-1.5 w-full !h-8 cursor-pointer"
        >
          <span>All notifications</span>
          <ArrowRightIcon className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );

  return (
    <>
      <Dropdown
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        popupRender={() => menuContent}
        trigger={["click"]}
        placement="bottomRight"
      >
        <button
          type="button"
          aria-label="View notifications"
          aria-expanded={dropdownOpen}
          aria-haspopup="dialog"
          className="relative inline-flex h-11 w-11 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 transition-[border-color,background-color,transform] duration-150 hover:border-(--brand-primary)/50 hover:bg-[#FBFBFB] hover:text-[#131B2A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--brand-primary)/50 active:scale-[0.98] cursor-pointer"
        >
          <Badge
            count={unreadCount}
            overflowCount={99}
            size="small"
            offset={[2, -2]}
            color="#E11D48"
          >
            <BellIcon className="h-4 w-4" strokeWidth={1.9} />
          </Badge>
        </button>
      </Dropdown>

      <CreateReminderModal
        open={reminderModalOpen}
        onClose={() => setReminderModalOpen(false)}
      />

      <ViewNotificationModal
        open={!!selectedNotification}
        onClose={() => setSelectedNotification(null)}
        notification={selectedNotification}
      />
    </>
  );
};
